import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Divider,
    Grid,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tabs,
    Tag,
    Typography
} from 'antd';

import {
    SearchOutlined,
    SyncOutlined,
    RollbackOutlined,
    ReloadOutlined,
    EyeOutlined,
    HistoryOutlined
} from '@ant-design/icons';

import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { apiCall } from '../../API/RestApi';
import { currencyFormat } from '../../Utils/formatCurrency';

const { RangePicker } = DatePicker;

function AccurateCustom() {
    const token = useSelector((state) => state.user.token);

    const screens = Grid.useBreakpoint();
    const compact = !screens.md;

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    const [activeTab, setActiveTab] = useState('transaction');

    // =====================================================
    // TRANSACTION
    // =====================================================

    const [transactionCode, setTransactionCode] = useState('');
    const [transaction, setTransaction] = useState(null);
    const [transactionItems, setTransactionItems] = useState([]);

    const [transactionDate, setTransactionDate] = useState(null);

    const [
        transactionGrandTotal,
        setTransactionGrandTotal
    ] = useState(0);

    const [
        nextInvoiceNumber,
        setNextInvoiceNumber
    ] = useState('');

    // =====================================================
    // MANUAL RETURN
    // =====================================================

    const [
        showReturnAccurate,
        setShowReturnAccurate
    ] = useState(false);

    const [returnItems, setReturnItems] = useState([]);

    const [
        returnAccurateDate,
        setReturnAccurateDate
    ] = useState(moment().format('YYYY-MM-DD'));

    const [returnCode, setReturnCode] = useState('');

    const [
        returnDescription,
        setReturnDescription
    ] = useState('');

    const [
        returnPointRedeem,
        setReturnPointRedeem
    ] = useState(0);

    const [
        returnVoucher,
        setReturnVoucher
    ] = useState(0);

    const [
        returnTotalAmount,
        setReturnTotalAmount
    ] = useState(0);

    // =====================================================
    // HISTORY
    // =====================================================

    const [
        historyLoading,
        setHistoryLoading
    ] = useState(false);

    const [
        historyData,
        setHistoryData
    ] = useState([]);

    const [
        historyTotal,
        setHistoryTotal
    ] = useState(0);

    const [
        historyPage,
        setHistoryPage
    ] = useState(1);

    const [
        historyPageSize,
        setHistoryPageSize
    ] = useState(10);

    const [
        historySearch,
        setHistorySearch
    ] = useState('');

    const [
        historyType,
        setHistoryType
    ] = useState('');

    const [
        historyStatus,
        setHistoryStatus
    ] = useState('');

    const [
        historyDateRange,
        setHistoryDateRange
    ] = useState(null);

    const [
        historyDetail,
        setHistoryDetail
    ] = useState(null);

    const [
        historyDetailOpen,
        setHistoryDetailOpen
    ] = useState(false);

    // =====================================================
    // API
    // =====================================================

    const request = async (
        method,
        url,
        data = null
    ) => {
        const res = await apiCall({
            method,
            url,
            data: {
                data,
                headers: {
                    Authorization:
                        'Bearer ' + token
                }
            }
        });

        if (!res) {
            throw new Error(
                'Tidak ada response dari server'
            );
        }

        return res;
    };

    const showError = (text) => {
        setMessage({
            type: 'error',
            text
        });
    };

    const showSuccess = (text) => {
        setMessage({
            type: 'success',
            text
        });
    };

    // =====================================================
    // TRANSACTION PRODUCT
    // =====================================================

    const buildTransactionItems = (trx) => {
        return (
            trx.transaction_products || []
        ).map((item, index) => {
            let price =
                Number(item.price || 0);

            let totalDiscount = 0;

            if (
                item.discount &&
                item.discount.price_after_discount != null &&
                item.discount.discount_price != null
            ) {
                price =
                    Number(
                        item.discount.price_after_discount || 0
                    ) +
                    Number(
                        item.discount.discount_price || 0
                    );

                totalDiscount =
                    Number(item.qty || 0) *
                    Number(
                        item.discount.discount_price || 0
                    );
            }

            return {
                key:
                    item.transaction_product_id ||
                    `transaction-${index}`,

                transaction_product_id:
                    item.transaction_product_id,

                product_id:
                    Number(
                        item.product_id ||
                        item.product?.product_id ||
                        0
                    ),

                product_code:
                    item.product?.product_code ||
                    item.product_code ||
                    '-',

                product_name:
                    item.product?.product_name ||
                    item.product_name ||
                    '-',

                variant_product_id:
                    Number(
                        item.variant_product_id ||
                        item.variant_product?.variant_product_id ||
                        0
                    ),

                variant_name:
                    item.variant_product?.variant_name ||
                    item.variant_name ||
                    '',

                qty:
                    Number(item.qty || 0),

                price,

                total_discount:
                    totalDiscount,

                warehouse_id:
                    item.warehouse_id ||
                    item.warehouse?.warehouse_id ||
                    trx.outlet?.warehouse_id,

                warehouse_name:
                    item.warehouse?.warehouse_name ||
                    '-',

                is_deleted:
                    item.is_deleted === true
            };
        });
    };

    const calcTransactionGrandTotal = (
        items,
        trx
    ) => {
        const itemNet =
            items.reduce(
                (sum, item) => {
                    return (
                        sum +
                        (
                            Number(item.qty || 0) *
                            Number(item.price || 0)
                        ) -
                        Number(
                            item.total_discount || 0
                        )
                    );
                },
                0
            );

        return Math.max(
            0,
            itemNet -
            Number(
                trx?.voucher_price || 0
            ) -
            Number(
                trx?.point_redeem || 0
            )
        );
    };

    // =====================================================
    // RETURN PRODUCT
    // =====================================================

    const buildManualReturnItems = (trx) => {
        return (
            trx.transaction_products || []
        ).map((item, index) => {
            let price =
                Number(item.price || 0);

            let totalDiscount = 0;

            if (
                item.discount &&
                item.discount.price_after_discount != null &&
                item.discount.discount_price != null
            ) {
                price =
                    Number(
                        item.discount.price_after_discount || 0
                    ) +
                    Number(
                        item.discount.discount_price || 0
                    );

                totalDiscount =
                    Number(item.qty || 0) *
                    Number(
                        item.discount.discount_price || 0
                    );
            }

            return {
                key:
                    item.transaction_product_id ||
                    `manual-return-${index}`,

                selected: false,

                transaction_product_id:
                    item.transaction_product_id,

                product_id:
                    Number(
                        item.product_id ||
                        item.product?.product_id ||
                        0
                    ),

                product_code:
                    item.product?.product_code ||
                    item.product_code ||
                    '-',

                product_name:
                    item.product?.product_name ||
                    item.product_name ||
                    '-',

                variant_product_id:
                    Number(
                        item.variant_product_id ||
                        item.variant_product?.variant_product_id ||
                        0
                    ),

                variant_name:
                    item.variant_product?.variant_name ||
                    item.variant_name ||
                    '',

                max_qty:
                    Number(item.qty || 0),

                qty:
                    Number(item.qty || 0),

                price,

                total_discount:
                    totalDiscount,

                warehouse_id:
                    item.warehouse_id ||
                    item.warehouse?.warehouse_id ||
                    trx.outlet?.warehouse_id,

                warehouse_name:
                    item.warehouse?.warehouse_name ||
                    '-',

                is_deleted:
                    item.is_deleted === true
            };
        });
    };

    // =====================================================
    // CALCULATE RETURN
    // =====================================================

    const calculateReturnTotal = (
        items = returnItems,
        point = returnPointRedeem,
        voucher = returnVoucher
    ) => {
        const selectedItems =
            items.filter(
                (item) =>
                    item.selected === true
            );

        const productTotal =
            selectedItems.reduce(
                (total, item) => {
                    const subtotal =
                        (
                            Number(item.qty || 0) *
                            Number(item.price || 0)
                        ) -
                        Number(
                            item.total_discount || 0
                        );

                    return (
                        total +
                        Math.max(
                            0,
                            subtotal
                        )
                    );
                },
                0
            );

        const total =
            Math.max(
                0,
                productTotal -
                Number(point || 0) -
                Number(voucher || 0)
            );

        setReturnTotalAmount(total);
    };

    // =====================================================
    // HISTORY
    // =====================================================

    const getCustomHistory = async ({
        page = historyPage,
        pageSize = historyPageSize,
        search = historySearch,
        type = historyType,
        status = historyStatus,
        dateRange = historyDateRange
    } = {}) => {
        setHistoryLoading(true);

        try {
            const params =
                new URLSearchParams();

            params.set(
                'page',
                String(page)
            );

            params.set(
                'limit',
                String(pageSize)
            );

            if (search?.trim()) {
                params.set(
                    'search',
                    search.trim()
                );
            }

            if (type) {
                params.set(
                    'type',
                    type
                );
            }

            if (status) {
                params.set(
                    'status',
                    status
                );
            }

            if (
                dateRange &&
                dateRange.length === 2
            ) {
                params.set(
                    'start_date',
                    dateRange[0].format(
                        'YYYY-MM-DD'
                    )
                );

                params.set(
                    'end_date',
                    dateRange[1].format(
                        'YYYY-MM-DD'
                    )
                );
            }

            const res =
                await request(
                    'GET',
                    `/accurate/custom-history?${params.toString()}`
                );

            if (
                res?.status &&
                Number(res.status) !== 200
            ) {
                throw new Error(
                    res?.message ||
                    'Gagal mengambil riwayat.'
                );
            }

            setHistoryData(
                res?.data?.rows || []
            );

            setHistoryTotal(
                Number(
                    res?.data?.count || 0
                )
            );

            setHistoryPage(
                Number(
                    res?.data?.page ||
                    page
                )
            );

            setHistoryPageSize(
                Number(
                    res?.data?.limit ||
                    pageSize
                )
            );

        } catch (error) {
            showError(
                error.message ||
                'Gagal mengambil riwayat custom Accurate.'
            );

            setHistoryData([]);
            setHistoryTotal(0);

        } finally {
            setHistoryLoading(false);
        }
    };

    const searchCustomHistory = () => {
        setHistoryPage(1);

        getCustomHistory({
            page: 1
        });
    };

    const resetHistoryFilter = () => {
        setHistorySearch('');
        setHistoryType('');
        setHistoryStatus('');
        setHistoryDateRange(null);
        setHistoryPage(1);

        getCustomHistory({
            page: 1,
            search: '',
            type: '',
            status: '',
            dateRange: null
        });
    };

    const openHistoryDetail = async (
        record
    ) => {
        setHistoryLoading(true);

        try {
            const res =
                await request(
                    'GET',
                    `/accurate/custom-history/${record.accurate_custom_history_id}`
                );

            if (
                res?.status &&
                Number(res.status) !== 200
            ) {
                throw new Error(
                    res?.message ||
                    'Detail history tidak ditemukan.'
                );
            }

            setHistoryDetail(
                res?.data ||
                record
            );

            setHistoryDetailOpen(
                true
            );

        } catch (error) {
            showError(
                error.message ||
                'Gagal mengambil detail history.'
            );

        } finally {
            setHistoryLoading(false);
        }
    };

    // =====================================================
    // SEARCH TRANSACTION
    // =====================================================

    const searchTransaction = async () => {
        const code =
            transactionCode.trim();

        if (!code) {
            return showError(
                'Masukkan kode transaksi.'
            );
        }

        setLoading(true);
        setMessage(null);

        try {
            const res =
                await request(
                    'GET',
                    `/transaction?transcode=${encodeURIComponent(
                        code
                    )}&customAccurate=true`
                );

            const rows =
                res?.data?.rows ||
                res?.data?.data ||
                [];

            const trx =
                rows.find(
                    (item) =>
                        String(
                            item.transaction_code || ''
                        ).toLowerCase() ===
                        code.toLowerCase()
                ) ||
                rows[0];

            if (!trx) {
                throw new Error(
                    'Transaksi tidak ditemukan.'
                );
            }

            // =============================================
            // TRANSACTION
            // =============================================

            const items =
                buildTransactionItems(
                    trx
                );

            setTransaction(trx);

            setTransactionItems(
                items
            );

            setTransactionDate(
                moment(
                    trx.trans_date ||
                    trx.created_at
                ).format(
                    'YYYY-MM-DD'
                )
            );

            setTransactionGrandTotal(
                calcTransactionGrandTotal(
                    items,
                    trx
                )
            );

            // =============================================
            // RETURN ITEMS
            // =============================================

            const manualReturnItems =
                buildManualReturnItems(
                    trx
                );

            setReturnItems(
                manualReturnItems
            );

            setReturnAccurateDate(
                moment().format(
                    'YYYY-MM-DD'
                )
            );

            setReturnDescription('');

            setReturnPointRedeem(
                Number(
                    trx.point_redeem ||
                    0
                )
            );

            setReturnVoucher(
                Number(
                    trx.voucher_price ||
                    0
                )
            );

            setReturnTotalAmount(0);

            setShowReturnAccurate(
                false
            );

            // =============================================
            // NEXT INVOICE
            // =============================================

            setNextInvoiceNumber('');

            try {
                const invoiceRes =
                    await request(
                        'GET',
                        `/accurate/custom-next-invoice?transaction_code=${encodeURIComponent(
                            trx.transaction_code
                        )}`
                    );

                setNextInvoiceNumber(
                    invoiceRes
                        ?.invoice_number ||
                    invoiceRes
                        ?.data
                        ?.invoice_number ||
                    `${trx.transaction_code}-1`
                );

            } catch (error) {
                setNextInvoiceNumber(
                    `${trx.transaction_code}-1`
                );
            }

            // =============================================
            // NEXT RETURN NUMBER
            // =============================================

            try {
                const returnRes =
                    await request(
                        'GET',
                        `/accurate/custom-next-return?transaction_code=${encodeURIComponent(
                            trx.transaction_code
                        )}`
                    );

                setReturnCode(
                    returnRes
                        ?.return_number ||
                    returnRes
                        ?.data
                        ?.return_number ||
                    ''
                );

            } catch (error) {
                setReturnCode('');
            }

        } catch (error) {
            setTransaction(null);
            setTransactionItems([]);
            setReturnItems([]);
            setNextInvoiceNumber('');
            setShowReturnAccurate(false);

            showError(
                error.message ||
                'Gagal mengambil transaksi.'
            );

        } finally {
            setLoading(false);
        }
    };

    // =====================================================
    // TRANSACTION ITEM UPDATE
    // =====================================================

    const updateTransactionItem = (
        key,
        field,
        value
    ) => {
        const next =
            transactionItems.map(
                (item) =>
                    item.key === key
                        ? {
                            ...item,
                            [field]:
                                Number(
                                    value || 0
                                )
                        }
                        : item
            );

        setTransactionItems(
            next
        );

        setTransactionGrandTotal(
            calcTransactionGrandTotal(
                next,
                transaction
            )
        );
    };

    // =====================================================
    // RETURN ITEM
    // =====================================================

    const toggleReturnItem = (
        key
    ) => {
        const next =
            returnItems.map(
                (item) =>
                    item.key === key
                        ? {
                            ...item,
                            selected:
                                !item.selected
                        }
                        : item
            );

        setReturnItems(next);

        calculateReturnTotal(
            next
        );
    };

    const toggleSelectAllReturn = (
        checked
    ) => {
        const next =
            returnItems.map(
                (item) => ({
                    ...item,
                    selected:
                        checked
                })
            );

        setReturnItems(next);

        calculateReturnTotal(
            next
        );
    };

    const updateManualReturnItem = (
        key,
        field,
        value
    ) => {
        const next =
            returnItems.map(
                (item) => {
                    if (
                        item.key !== key
                    ) {
                        return item;
                    }

                    let newValue =
                        Number(
                            value || 0
                        );

                    if (
                        field === 'qty'
                    ) {
                        newValue =
                            Math.max(
                                0,
                                Math.min(
                                    newValue,
                                    Number(
                                        item.max_qty || 0
                                    )
                                )
                            );
                    }

                    return {
                        ...item,
                        [field]:
                            newValue
                    };
                }
            );

        setReturnItems(next);

        calculateReturnTotal(
            next
        );
    };

    // =====================================================
    // SEND TRANSACTION
    // =====================================================

    const sendTransaction = () => {
        if (
            !transaction ||
            !transactionDate
        ) {
            return showError(
                'Pilih transaksi dan tanggal Accurate.'
            );
        }

        const invoicePreview =
            nextInvoiceNumber ||
            `${transaction.transaction_code}-1`;

        Modal.confirm({
            title:
                'Kirim ulang transaksi ke Accurate?',

            width: 520,

            content: (
                <div>
                    <p>
                        Transaction Code
                        <br />
                        <b>
                            {
                                transaction.transaction_code
                            }
                        </b>
                    </p>

                    <p>
                        Invoice Accurate Baru
                        <br />
                        <b>
                            {
                                invoicePreview
                            }
                        </b>
                    </p>

                    <p>
                        Tanggal Accurate
                        <br />
                        <b>
                            {moment(
                                transactionDate
                            ).format(
                                'DD/MM/YYYY'
                            )}
                        </b>
                    </p>

                    <p>
                        Total
                        <br />
                        <b>
                            {currencyFormat(
                                transactionGrandTotal
                            )}
                        </b>
                    </p>

                    <Alert
                        type="info"
                        showIcon
                        message="Data transaksi POS asli tidak diubah."
                    />
                </div>
            ),

            okText:
                'Kirim ke Accurate',

            cancelText:
                'Batal',

            onOk: async () => {
                setLoading(true);
                setMessage(null);

                try {
                    const payload = {
                        outlet_id:
                            transaction.outlet_id,

                        outlet_code:
                            transaction.outlet
                                ?.outlet_code,

                        outlet_name:
                            transaction.outlet
                                ?.outlet_name,

                        branch_name:
                            transaction.outlet
                                ?.branch_name,

                        transaction_id:
                            transaction.transaction_id,

                        transaction_code:
                            transaction.transaction_code,

                        custom_resend:
                            true,

                        voucher_price:
                            Number(
                                transaction.voucher_price ||
                                0
                            ),

                        point_redeem:
                            Number(
                                transaction.point_redeem ||
                                0
                            ),

                        payment_method:
                            transaction.payment_method,

                        device_payment:
                            transaction.device_payment,

                        trans_date:
                            moment(
                                transactionDate
                            ).format(
                                'DD/MM/YYYY'
                            ),

                        grand_total:
                            Number(
                                transactionGrandTotal ||
                                0
                            ),

                        notes:
                            `${transaction.notes || ''} ` +
                            `[CUSTOM ACCURATE ${moment().format(
                                'DD/MM/YYYY HH:mm'
                            )}]`,

                        akun_no:
                            transaction.akun_no,

                        item:
                            transactionItems.map(
                                (item) => ({
                                    product_id:
                                        item.product_id,

                                    product_code:
                                        item.product_code,

                                    variant_product_id:
                                        item.variant_product_id,

                                    variant_name:
                                        item.variant_name,

                                    qty:
                                        Number(
                                            item.qty || 0
                                        ),

                                    price:
                                        Number(
                                            item.price || 0
                                        ),

                                    total_discount:
                                        Number(
                                            item.total_discount ||
                                            0
                                        ),

                                    warehouse_id:
                                        item.warehouse_id,

                                    warehouse_name:
                                        item.warehouse_name,

                                    subtotal:
                                        (
                                            Number(
                                                item.qty || 0
                                            ) *
                                            Number(
                                                item.price || 0
                                            )
                                        ) -
                                        Number(
                                            item.total_discount ||
                                            0
                                        )
                                })
                            )
                    };

                    const res =
                        await request(
                            'POST',
                            '/accurate/custom-transaction',
                            payload
                        );

                    if (
                        res?.respon === false
                    ) {
                        throw new Error(
                            res?.message ||
                            'Accurate menolak transaksi.'
                        );
                    }

                    if (
                        res?.status &&
                        Number(
                            res.status
                        ) !== 200
                    ) {
                        throw new Error(
                            res?.message ||
                            'Accurate menolak transaksi.'
                        );
                    }

                    const sentInvoice =
                        res?.invoice_number ||
                        res?.data?.invoice_number ||
                        invoicePreview;

                    showSuccess(
                        `Transaksi berhasil dikirim ke Accurate sebagai ${sentInvoice}.`
                    );

                    // refresh next invoice
                    try {
                        const nextRes =
                            await request(
                                'GET',
                                `/accurate/custom-next-invoice?transaction_code=${encodeURIComponent(
                                    transaction.transaction_code
                                )}`
                            );

                        setNextInvoiceNumber(
                            nextRes
                                ?.invoice_number ||
                            nextRes
                                ?.data
                                ?.invoice_number ||
                            ''
                        );
                    } catch (error) {
                        setNextInvoiceNumber('');
                    }

                    // refresh history
                    getCustomHistory({
                        page: 1
                    });

                } catch (error) {
                    showError(
                        error.message ||
                        'Gagal mengirim transaksi ke Accurate.'
                    );

                    getCustomHistory({
                        page: 1
                    });

                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // =====================================================
    // SEND RETURN
    // =====================================================

    const sendManualReturn = () => {
        if (!transaction) {
            return showError(
                'Transaksi belum dipilih.'
            );
        }

        const selectedItems =
            returnItems.filter(
                (item) =>
                    item.selected &&
                    Number(
                        item.qty || 0
                    ) > 0
            );

        if (
            selectedItems.length === 0
        ) {
            return showError(
                'Pilih minimal 1 produk yang akan diretur.'
            );
        }

        if (!returnCode.trim()) {
            return showError(
                'No Retur Accurate wajib diisi.'
            );
        }

        if (!returnAccurateDate) {
            return showError(
                'Tanggal retur Accurate wajib diisi.'
            );
        }

        Modal.confirm({
            title:
                'Kirim retur transaksi ke Accurate?',

            width: 520,

            content: (
                <div>
                    <p>
                        Transaction Code
                        <br />
                        <b>
                            {
                                transaction.transaction_code
                            }
                        </b>
                    </p>

                    <p>
                        No Retur Accurate
                        <br />
                        <b>
                            {returnCode}
                        </b>
                    </p>

                    <p>
                        Tanggal Retur
                        <br />
                        <b>
                            {moment(
                                returnAccurateDate
                            ).format(
                                'DD/MM/YYYY'
                            )}
                        </b>
                    </p>

                    <p>
                        Produk diretur
                        <br />
                        <b>
                            {selectedItems.length} produk
                        </b>
                    </p>

                    <p>
                        Total Retur
                        <br />
                        <b>
                            {currencyFormat(
                                returnTotalAmount
                            )}
                        </b>
                    </p>

                    <Alert
                        type="warning"
                        showIcon
                        message="Retur hanya dikirim ke Accurate. Data POS tidak diubah."
                    />
                </div>
            ),

            okText:
                'Kirim Retur Accurate',

            cancelText:
                'Batal',

            onOk: async () => {
                setLoading(true);
                setMessage(null);

                try {
                    const itemReturn =
                        selectedItems.map(
                            (item) => {
                                const subtotal =
                                    (
                                        Number(
                                            item.qty || 0
                                        ) *
                                        Number(
                                            item.price || 0
                                        )
                                    ) -
                                    Number(
                                        item.total_discount ||
                                        0
                                    );

                                return {
                                    product_code:
                                        item.product_code,

                                    product_id:
                                        item.product_id,

                                    variant_product_id:
                                        item.variant_product_id,

                                    qty:
                                        Number(
                                            item.qty || 0
                                        ),

                                    price:
                                        Number(
                                            item.price || 0
                                        ),

                                    total_discount:
                                        Number(
                                            item.total_discount ||
                                            0
                                        ),

                                    price_payment:
                                        Math.max(
                                            0,
                                            subtotal
                                        ),

                                    subtotal:
                                        Math.max(
                                            0,
                                            subtotal
                                        ),

                                    warehouse_id:
                                        item.warehouse_id,

                                    warehouse_name:
                                        item.warehouse_name
                                };
                            }
                        );

                    const payload = {
                        transaction_id:
                            transaction.transaction_id,

                        trans_date:
                            returnAccurateDate,

                        return_date:
                            returnAccurateDate,

                        transaction_code:
                            transaction.transaction_code,

                        outlet_id:
                            transaction.outlet_id,

                        outlet_code:
                            transaction.outlet
                                ?.outlet_code,

                        outlet_name:
                            transaction.outlet
                                ?.outlet_name,

                        branch_name:
                            transaction.outlet
                                ?.branch_name,

                        return_code:
                            returnCode.trim(),

                        item_return:
                            itemReturn,

                        desc:
                            returnDescription.trim() ||
                            `CUSTOM RETURN ACCURATE ${transaction.transaction_code}`,

                        akun_no:
                            transaction.akun_no,

                        point_redeem:
                            Number(
                                returnPointRedeem ||
                                0
                            ),

                        voucher_num:
                            Number(
                                returnVoucher ||
                                0
                            ),

                        total_amount:
                            Number(
                                returnTotalAmount ||
                                0
                            )
                    };

                    const res =
                        await request(
                            'POST',
                            '/accurate/productreturn-custom',
                            payload
                        );

                    if (
                        res?.respon === false
                    ) {
                        throw new Error(
                            res?.message ||
                            'Accurate menolak retur.'
                        );
                    }

                    if (
                        res?.status &&
                        Number(
                            res.status
                        ) !== 200
                    ) {
                        throw new Error(
                            res?.message ||
                            'Accurate menolak retur.'
                        );
                    }

                    const sentReturnCode =
                        res?.return_code ||
                        returnCode;

                    showSuccess(
                        `Retur ${sentReturnCode} berhasil dikirim ke Accurate.`
                    );

                    setShowReturnAccurate(
                        false
                    );

                    // next return
                    try {
                        const nextReturn =
                            await request(
                                'GET',
                                `/accurate/custom-next-return?transaction_code=${encodeURIComponent(
                                    transaction.transaction_code
                                )}`
                            );

                        setReturnCode(
                            nextReturn
                                ?.return_number ||
                            ''
                        );

                    } catch (error) {
                        setReturnCode('');
                    }

                    getCustomHistory({
                        page: 1
                    });

                } catch (error) {
                    showError(
                        error.message ||
                        'Gagal mengirim retur ke Accurate.'
                    );

                    getCustomHistory({
                        page: 1
                    });

                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // =====================================================
    // TRANSACTION TABLE
    // =====================================================

    const transactionColumns = [
        {
            title: 'SKU',
            dataIndex: 'product_code',
            width: 140,

            render: (value) => (
                <Typography.Text code>
                    {value || '-'}
                </Typography.Text>
            )
        },

        {
            title: 'Produk',
            width: 280,

            render: (_, item) => (
                <Space
                    direction="vertical"
                    size={3}
                >
                    <Typography.Text strong>
                        {item.product_name}
                    </Typography.Text>

                    <Space
                        wrap
                        size={5}
                    >
                        {item.variant_name && (
                            <Typography.Text
                                type="secondary"
                            >
                                {item.variant_name}
                            </Typography.Text>
                        )}

                        {item.is_deleted && (
                            <Tag color="orange">
                                Sudah Diretur
                            </Tag>
                        )}
                    </Space>
                </Space>
            )
        },

        {
            title: 'Qty',
            dataIndex: 'qty',
            width: 80,
            align: 'center'
        },

        {
            title:
                'Harga Accurate',

            width: 180,

            render: (_, item) => (
                <InputNumber
                    min={0}
                    value={item.price}
                    style={{
                        width: '100%'
                    }}
                    onChange={(value) =>
                        updateTransactionItem(
                            item.key,
                            'price',
                            value
                        )
                    }
                />
            )
        },

        {
            title:
                'Diskon Total',

            width: 180,

            render: (_, item) => (
                <InputNumber
                    min={0}
                    value={
                        item.total_discount
                    }
                    style={{
                        width: '100%'
                    }}
                    onChange={(value) =>
                        updateTransactionItem(
                            item.key,
                            'total_discount',
                            value
                        )
                    }
                />
            )
        },

        {
            title: 'Net',
            width: 160,
            align: 'right',

            render: (_, item) => (
                <Typography.Text strong>
                    {currencyFormat(
                        (
                            Number(
                                item.qty || 0
                            ) *
                            Number(
                                item.price || 0
                            )
                        ) -
                        Number(
                            item.total_discount ||
                            0
                        )
                    )}
                </Typography.Text>
            )
        }
    ];

    // =====================================================
    // RETURN TABLE
    // =====================================================

    const allReturnSelected =
        returnItems.length > 0 &&
        returnItems.every(
            (item) =>
                item.selected === true
        );

    const someReturnSelected =
        returnItems.some(
            (item) =>
                item.selected === true
        );

    const returnColumns = [
        {
            title: (
                <Checkbox
                    checked={
                        allReturnSelected
                    }
                    indeterminate={
                        someReturnSelected &&
                        !allReturnSelected
                    }
                    onChange={(e) =>
                        toggleSelectAllReturn(
                            e.target.checked
                        )
                    }
                />
            ),

            width: 55,
            align: 'center',

            render: (_, item) => (
                <Checkbox
                    checked={
                        item.selected
                    }
                    onChange={() =>
                        toggleReturnItem(
                            item.key
                        )
                    }
                />
            )
        },

        {
            title: 'SKU',
            dataIndex: 'product_code',
            width: 140,

            render: (value) => (
                <Typography.Text code>
                    {value || '-'}
                </Typography.Text>
            )
        },

        {
            title: 'Produk',
            width: 280,

            render: (_, item) => (
                <Space
                    direction="vertical"
                    size={3}
                >
                    <Typography.Text strong>
                        {item.product_name}
                    </Typography.Text>

                    <Space wrap size={5}>
                        {item.variant_name && (
                            <Typography.Text
                                type="secondary"
                            >
                                {item.variant_name}
                            </Typography.Text>
                        )}

                        {item.is_deleted && (
                            <Tag color="orange">
                                Pernah Diretur
                            </Tag>
                        )}
                    </Space>
                </Space>
            )
        },

        {
            title:
                'Qty Transaksi',

            dataIndex:
                'max_qty',

            width: 120,
            align: 'center'
        },

        {
            title:
                'Qty Retur',

            width: 130,

            render: (_, item) => (
                <InputNumber
                    min={0}
                    max={
                        item.max_qty
                    }
                    disabled={
                        !item.selected
                    }
                    value={
                        item.qty
                    }
                    style={{
                        width: '100%'
                    }}
                    onChange={(value) =>
                        updateManualReturnItem(
                            item.key,
                            'qty',
                            value
                        )
                    }
                />
            )
        },

        {
            title:
                'Harga Retur',

            width: 170,

            render: (_, item) => (
                <InputNumber
                    min={0}
                    disabled={
                        !item.selected
                    }
                    value={
                        item.price
                    }
                    style={{
                        width: '100%'
                    }}
                    onChange={(value) =>
                        updateManualReturnItem(
                            item.key,
                            'price',
                            value
                        )
                    }
                />
            )
        },

        {
            title:
                'Diskon Total',

            width: 170,

            render: (_, item) => (
                <InputNumber
                    min={0}
                    disabled={
                        !item.selected
                    }
                    value={
                        item.total_discount
                    }
                    style={{
                        width: '100%'
                    }}
                    onChange={(value) =>
                        updateManualReturnItem(
                            item.key,
                            'total_discount',
                            value
                        )
                    }
                />
            )
        },

        {
            title: 'Subtotal',
            width: 160,
            align: 'right',

            render: (_, item) => {
                if (!item.selected) {
                    return '-';
                }

                return (
                    <Typography.Text strong>
                        {currencyFormat(
                            Math.max(
                                0,
                                (
                                    Number(
                                        item.qty || 0
                                    ) *
                                    Number(
                                        item.price || 0
                                    )
                                ) -
                                Number(
                                    item.total_discount ||
                                    0
                                )
                            )
                        )}
                    </Typography.Text>
                );
            }
        }
    ];

    // =====================================================
    // HISTORY TABLE
    // =====================================================

    const historyColumns = [
        {
            title: 'Waktu',
            dataIndex: 'created_at',
            width: 170,

            render: (value) =>
                value
                    ? moment(value).format(
                        'DD/MM/YYYY HH:mm'
                    )
                    : '-'
        },

        {
            title: 'Tipe',
            dataIndex: 'type',
            width: 120,

            render: (value) =>
                String(value).toUpperCase() ===
                'RETURN'
                    ? (
                        <Tag color="volcano">
                            RETURN
                        </Tag>
                    )
                    : (
                        <Tag color="blue">
                            TRANSACTION
                        </Tag>
                    )
        },

        {
            title:
                'Transaction Code',

            dataIndex:
                'transaction_code',

            width: 230,

            render: (value) => (
                <Typography.Text
                    code
                    copyable
                >
                    {value || '-'}
                </Typography.Text>
            )
        },

        {
            title:
                'Document No',

            dataIndex:
                'document_number',

            width: 240,

            render: (value) => (
                <Typography.Text
                    strong
                    copyable
                >
                    {value || '-'}
                </Typography.Text>
            )
        },

        {
            title:
                'Tanggal Accurate',

            dataIndex:
                'trans_date',

            width: 150,

            render: (value) =>
                value
                    ? moment(value).format(
                        'DD/MM/YYYY'
                    )
                    : '-'
        },

        {
            title: 'Total',
            dataIndex: 'total_amount',
            width: 160,
            align: 'right',

            render: (value) => (
                <Typography.Text strong>
                    {currencyFormat(
                        Number(value || 0)
                    )}
                </Typography.Text>
            )
        },

        {
            title: 'Status',
            dataIndex: 'status',
            width: 110,

            render: (value) =>
                String(value).toUpperCase() ===
                'SUCCESS'
                    ? (
                        <Tag color="green">
                            SUCCESS
                        </Tag>
                    )
                    : (
                        <Tag color="red">
                            FAILED
                        </Tag>
                    )
        },

        {
            title: 'User',
            dataIndex: 'username',
            width: 140,

            render: (value) =>
                value || '-'
        },

        {
            title: 'Action',
            width: 105,
            fixed: 'right',

            render: (_, record) => (
                <Button
                    icon={
                        <EyeOutlined />
                    }
                    onClick={() =>
                        openHistoryDetail(
                            record
                        )
                    }
                >
                    Detail
                </Button>
            )
        }
    ];

    // =====================================================
    // STYLE
    // =====================================================

    const infoBoxStyle = {
        padding: '14px 16px',
        border:
            '1px solid #f0f0f0',
        borderRadius: 12,
        height: '100%',
        background:
            '#fafafa'
    };

    const cardStyle = {
        borderRadius: 16,
        boxShadow:
            '0 4px 20px rgba(0,0,0,0.04)'
    };

    // =====================================================
    // TRANSACTION PAGE
    // =====================================================

    const transactionContent = (
        <Spin spinning={loading}>
            <Card
                style={cardStyle}
                title={
                    <Space>
                        <SearchOutlined />
                        <span>
                            Cari Transaksi
                        </span>
                    </Space>
                }
            >
                <Space.Compact
                    style={{
                        width: '100%',
                        maxWidth: 680
                    }}
                >
                    <Input
                        allowClear
                        size="large"
                        placeholder="Contoh: P.20260908.123772.000001"
                        value={
                            transactionCode
                        }
                        onChange={(e) =>
                            setTransactionCode(
                                e.target.value
                            )
                        }
                        onPressEnter={
                            searchTransaction
                        }
                    />

                    <Button
                        size="large"
                        type="primary"
                        icon={
                            <SearchOutlined />
                        }
                        onClick={
                            searchTransaction
                        }
                    >
                        Cari
                    </Button>
                </Space.Compact>
            </Card>

            {transaction && (
                <Card
                    style={{
                        ...cardStyle,
                        marginTop: 18
                    }}
                    title={
                        <Space wrap>
                            <span>
                                Detail Transaksi
                            </span>

                            <Tag color="blue">
                                {
                                    transaction.transaction_code
                                }
                            </Tag>

                            {transaction.status ===
                                false && (
                                <Tag color="orange">
                                    Status POS:
                                    false
                                </Tag>
                            )}
                        </Space>
                    }
                >
                    <Row
                        gutter={[12, 12]}
                    >
                        <Col
                            xs={24}
                            sm={12}
                            lg={6}
                        >
                            <div
                                style={
                                    infoBoxStyle
                                }
                            >
                                <Typography.Text
                                    type="secondary"
                                >
                                    Outlet
                                </Typography.Text>

                                <br />

                                <Typography.Text
                                    strong
                                >
                                    {transaction
                                        .outlet
                                        ?.outlet_name ||
                                        '-'}
                                </Typography.Text>
                            </div>
                        </Col>

                        <Col
                            xs={24}
                            sm={12}
                            lg={6}
                        >
                            <div
                                style={
                                    infoBoxStyle
                                }
                            >
                                <Typography.Text
                                    type="secondary"
                                >
                                    Tanggal POS
                                </Typography.Text>

                                <br />

                                <Typography.Text
                                    strong
                                >
                                    {moment(
                                        transaction.created_at
                                    ).format(
                                        'DD/MM/YYYY HH:mm'
                                    )}
                                </Typography.Text>
                            </div>
                        </Col>

                        <Col
                            xs={24}
                            sm={12}
                            lg={6}
                        >
                            <div
                                style={
                                    infoBoxStyle
                                }
                            >
                                <Typography.Text
                                    type="secondary"
                                >
                                    Status Accurate
                                </Typography.Text>

                                <br />

                                {transaction.status_send ? (
                                    <Tag color="green">
                                        Sudah dikirim
                                    </Tag>
                                ) : (
                                    <Tag>
                                        Belum dikirim
                                    </Tag>
                                )}
                            </div>
                        </Col>

                        <Col
                            xs={24}
                            sm={12}
                            lg={6}
                        >
                            <div
                                style={
                                    infoBoxStyle
                                }
                            >
                                <Typography.Text
                                    type="secondary"
                                >
                                    Invoice Baru
                                </Typography.Text>

                                <br />

                                <Typography.Text
                                    strong
                                    copyable={
                                        !!nextInvoiceNumber
                                    }
                                >
                                    {nextInvoiceNumber ||
                                        'Memuat...'}
                                </Typography.Text>
                            </div>
                        </Col>
                    </Row>

                    <Divider
                        orientation="left"
                    >
                        Pengaturan Accurate
                    </Divider>

                    <Row
                        gutter={[16, 16]}
                        align="bottom"
                    >
                        <Col
                            xs={24}
                            md={8}
                        >
                            <Typography.Text
                                strong
                            >
                                Tanggal Accurate
                            </Typography.Text>

                            <Input
                                type="date"
                                size="large"
                                value={
                                    transactionDate ||
                                    ''
                                }
                                onChange={(e) =>
                                    setTransactionDate(
                                        e.target.value
                                    )
                                }
                                style={{
                                    width: '100%',
                                    marginTop: 6
                                }}
                            />
                        </Col>

                        <Col
                            xs={24}
                            md={8}
                        >
                            <Typography.Text
                                strong
                            >
                                Total Bayar Accurate
                            </Typography.Text>

                            <InputNumber
                                min={0}
                                size="large"
                                value={
                                    transactionGrandTotal
                                }
                                onChange={(value) =>
                                    setTransactionGrandTotal(
                                        Number(
                                            value || 0
                                        )
                                    )
                                }
                                style={{
                                    width: '100%',
                                    marginTop: 6
                                }}
                            />
                        </Col>

                        <Col
                            xs={24}
                            md={8}
                        >
                            <div
                                style={
                                    infoBoxStyle
                                }
                            >
                                <Space
                                    direction="vertical"
                                    size={4}
                                >
                                    <Typography.Text
                                        type="secondary"
                                    >
                                        Voucher
                                    </Typography.Text>

                                    <Typography.Text
                                        strong
                                    >
                                        {currencyFormat(
                                            transaction.voucher_price ||
                                            0
                                        )}
                                    </Typography.Text>

                                    <Typography.Text
                                        type="secondary"
                                    >
                                        Point
                                    </Typography.Text>

                                    <Typography.Text
                                        strong
                                    >
                                        {currencyFormat(
                                            transaction.point_redeem ||
                                            0
                                        )}
                                    </Typography.Text>
                                </Space>
                            </div>
                        </Col>
                    </Row>

                    <Divider
                        orientation="left"
                    >
                        Produk Transaksi (
                        {
                            transactionItems.length
                        }
                        )
                    </Divider>

                    <Alert
                        showIcon
                        type="info"
                        message="Semua produk transaksi ditampilkan termasuk item yang sudah diretur."
                        style={{
                            marginBottom: 14,
                            borderRadius: 10
                        }}
                    />

                    <Table
                        rowKey="key"
                        pagination={false}
                        size={
                            compact
                                ? 'small'
                                : 'middle'
                        }
                        columns={
                            transactionColumns
                        }
                        dataSource={
                            transactionItems
                        }
                        scroll={{
                            x: 950
                        }}
                    />

                    <Divider />

                    <Row
                        gutter={[12, 12]}
                        justify="end"
                    >
                        <Col
                            xs={24}
                            sm="auto"
                        >
                            <Button
                                size="large"
                                block={compact}
                                icon={
                                    <RollbackOutlined />
                                }
                                onClick={() =>
                                    setShowReturnAccurate(
                                        (value) =>
                                            !value
                                    )
                                }
                            >
                                {showReturnAccurate
                                    ? 'Tutup Retur Accurate'
                                    : 'Retur Accurate'}
                            </Button>
                        </Col>

                        <Col
                            xs={24}
                            sm="auto"
                        >
                            <Button
                                type="primary"
                                size="large"
                                block={compact}
                                icon={
                                    <SyncOutlined />
                                }
                                onClick={
                                    sendTransaction
                                }
                            >
                                Kirim Custom ke Accurate
                            </Button>
                        </Col>
                    </Row>
                </Card>
            )}

            {transaction &&
                showReturnAccurate && (
                <Card
                    style={{
                        ...cardStyle,
                        marginTop: 18
                    }}
                    title={
                        <Space wrap>
                            <RollbackOutlined />

                            <span>
                                Retur Accurate Manual
                            </span>

                            <Tag color="volcano">
                                {
                                    transaction.transaction_code
                                }
                            </Tag>
                        </Space>
                    }
                >
                    <Alert
                        showIcon
                        type="warning"
                        message="Retur manual langsung ke Accurate"
                        description="Pilih produk yang ingin diretur. Qty, harga, diskon, nomor retur, tanggal, point, voucher, total dan deskripsi bisa diubah manual."
                        style={{
                            marginBottom: 18,
                            borderRadius: 10
                        }}
                    />

                    <Row
                        gutter={[16, 16]}
                    >
                        <Col
                            xs={24}
                            md={8}
                        >
                            <Typography.Text
                                strong
                            >
                                No Retur Accurate
                            </Typography.Text>

                            <Input
                                size="large"
                                value={
                                    returnCode
                                }
                                onChange={(e) =>
                                    setReturnCode(
                                        e.target.value
                                    )
                                }
                                style={{
                                    marginTop: 6
                                }}
                            />
                        </Col>

                        <Col
                            xs={24}
                            md={8}
                        >
                            <Typography.Text
                                strong
                            >
                                Tanggal Retur
                            </Typography.Text>

                            <Input
                                type="date"
                                size="large"
                                value={
                                    returnAccurateDate
                                }
                                onChange={(e) =>
                                    setReturnAccurateDate(
                                        e.target.value
                                    )
                                }
                                style={{
                                    marginTop: 6
                                }}
                            />
                        </Col>

                        <Col
                            xs={24}
                            md={8}
                        >
                            <Typography.Text
                                strong
                            >
                                Transaction Code
                            </Typography.Text>

                            <Input
                                size="large"
                                disabled
                                value={
                                    transaction.transaction_code
                                }
                                style={{
                                    marginTop: 6
                                }}
                            />
                        </Col>
                    </Row>

                    <Divider
                        orientation="left"
                    >
                        Pilih Produk Retur
                    </Divider>

                    <Table
                        rowKey="key"
                        pagination={false}
                        size={
                            compact
                                ? 'small'
                                : 'middle'
                        }
                        columns={
                            returnColumns
                        }
                        dataSource={
                            returnItems
                        }
                        scroll={{
                            x: 1200
                        }}
                    />

                    <Divider
                        orientation="left"
                    >
                        Nilai Retur
                    </Divider>

                    <Row
                        gutter={[16, 16]}
                    >
                        <Col
                            xs={24}
                            md={8}
                        >
                            <Typography.Text
                                strong
                            >
                                Point Dikembalikan
                            </Typography.Text>

                            <InputNumber
                                min={0}
                                size="large"
                                value={
                                    returnPointRedeem
                                }
                                onChange={(value) => {
                                    const point =
                                        Number(
                                            value || 0
                                        );

                                    setReturnPointRedeem(
                                        point
                                    );

                                    calculateReturnTotal(
                                        returnItems,
                                        point,
                                        returnVoucher
                                    );
                                }}
                                style={{
                                    width: '100%',
                                    marginTop: 6
                                }}
                            />
                        </Col>

                        <Col
                            xs={24}
                            md={8}
                        >
                            <Typography.Text
                                strong
                            >
                                Voucher
                            </Typography.Text>

                            <InputNumber
                                min={0}
                                size="large"
                                value={
                                    returnVoucher
                                }
                                onChange={(value) => {
                                    const voucher =
                                        Number(
                                            value || 0
                                        );

                                    setReturnVoucher(
                                        voucher
                                    );

                                    calculateReturnTotal(
                                        returnItems,
                                        returnPointRedeem,
                                        voucher
                                    );
                                }}
                                style={{
                                    width: '100%',
                                    marginTop: 6
                                }}
                            />
                        </Col>

                        <Col
                            xs={24}
                            md={8}
                        >
                            <Typography.Text
                                strong
                            >
                                Total Retur
                            </Typography.Text>

                            <InputNumber
                                min={0}
                                size="large"
                                value={
                                    returnTotalAmount
                                }
                                onChange={(value) =>
                                    setReturnTotalAmount(
                                        Number(
                                            value || 0
                                        )
                                    )
                                }
                                style={{
                                    width: '100%',
                                    marginTop: 6
                                }}
                            />
                        </Col>
                    </Row>

                    <div
                        style={{
                            marginTop: 18
                        }}
                    >
                        <Typography.Text
                            strong
                        >
                            Deskripsi / Alasan Retur
                        </Typography.Text>

                        <Input.TextArea
                            rows={4}
                            value={
                                returnDescription
                            }
                            placeholder="Contoh: Koreksi harga Accurate / barang diretur / salah transaksi"
                            onChange={(e) =>
                                setReturnDescription(
                                    e.target.value
                                )
                            }
                            style={{
                                marginTop: 6
                            }}
                        />
                    </div>

                    <Divider />

                    <Row
                        gutter={[12, 12]}
                        align="middle"
                    >
                        <Col
                            xs={24}
                            md={12}
                        >
                            <Space
                                direction="vertical"
                                size={3}
                            >
                                <Typography.Text
                                    type="secondary"
                                >
                                    Produk dipilih
                                </Typography.Text>

                                <Typography.Title
                                    level={5}
                                    style={{
                                        margin: 0
                                    }}
                                >
                                    {
                                        returnItems.filter(
                                            (item) =>
                                                item.selected
                                        ).length
                                    } produk
                                </Typography.Title>

                                <Typography.Text
                                    type="secondary"
                                >
                                    Total Retur
                                </Typography.Text>

                                <Typography.Title
                                    level={4}
                                    style={{
                                        margin: 0
                                    }}
                                >
                                    {currencyFormat(
                                        returnTotalAmount
                                    )}
                                </Typography.Title>
                            </Space>
                        </Col>

                        <Col
                            xs={24}
                            md={12}
                        >
                            <div
                                style={{
                                    display:
                                        'flex',
                                    justifyContent:
                                        compact
                                            ? 'stretch'
                                            : 'flex-end'
                                }}
                            >
                                <Button
                                    danger
                                    type="primary"
                                    size="large"
                                    block={compact}
                                    icon={
                                        <SyncOutlined />
                                    }
                                    onClick={
                                        sendManualReturn
                                    }
                                >
                                    Kirim Retur ke Accurate
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Card>
            )}
        </Spin>
    );

    // =====================================================
    // HISTORY PAGE
    // =====================================================

    const historyContent = (
        <Card
            style={cardStyle}
            title={
                <Space>
                    <HistoryOutlined />
                    <span>
                        Riwayat Custom Accurate
                    </span>
                </Space>
            }
            extra={
                <Button
                    icon={
                        <ReloadOutlined />
                    }
                    loading={
                        historyLoading
                    }
                    onClick={() =>
                        getCustomHistory()
                    }
                >
                    Refresh
                </Button>
            }
        >
            <Row
                gutter={[12, 12]}
                align="bottom"
            >
                <Col
                    xs={24}
                    md={12}
                    lg={8}
                >
                    <Typography.Text
                        strong
                    >
                        Search
                    </Typography.Text>

                    <Input
                        allowClear
                        size="large"
                        prefix={
                            <SearchOutlined />
                        }
                        value={
                            historySearch
                        }
                        placeholder="Transaction code / document / user"
                        style={{
                            marginTop: 6
                        }}
                        onChange={(e) =>
                            setHistorySearch(
                                e.target.value
                            )
                        }
                        onPressEnter={
                            searchCustomHistory
                        }
                    />
                </Col>

                <Col
                    xs={24}
                    sm={12}
                    md={6}
                    lg={4}
                >
                    <Typography.Text
                        strong
                    >
                        Tipe
                    </Typography.Text>

                    <Select
                            virtual={false}
                        allowClear
                        size="large"
                        placeholder="Semua"
                        value={
                            historyType ||
                            undefined
                        }
                        style={{
                            width: '100%',
                            marginTop: 6
                        }}
                        options={[
                            {
                                value:
                                    'TRANSACTION',
                                label:
                                    'Transaction'
                            },
                            {
                                value:
                                    'RETURN',
                                label:
                                    'Return'
                            }
                        ]}
                        onChange={(value) =>
                            setHistoryType(
                                value || ''
                            )
                        }
                    />
                </Col>

                <Col
                    xs={24}
                    sm={12}
                    md={6}
                    lg={4}
                >
                    <Typography.Text
                        strong
                    >
                        Status
                    </Typography.Text>

                    <Select
                            virtual={false}
                        allowClear
                        size="large"
                        placeholder="Semua"
                        value={
                            historyStatus ||
                            undefined
                        }
                        style={{
                            width: '100%',
                            marginTop: 6
                        }}
                        options={[
                            {
                                value:
                                    'SUCCESS',
                                label:
                                    'Success'
                            },
                            {
                                value:
                                    'FAILED',
                                label:
                                    'Failed'
                            }
                        ]}
                        onChange={(value) =>
                            setHistoryStatus(
                                value || ''
                            )
                        }
                    />
                </Col>

                <Col
                    xs={24}
                    lg={8}
                >
                    <Typography.Text
                        strong
                    >
                        Tanggal
                    </Typography.Text>

                    <RangePicker
                        size="large"
                        value={
                            historyDateRange
                        }
                        format="DD/MM/YYYY"
                        style={{
                            width: '100%',
                            marginTop: 6
                        }}
                        onChange={(value) =>
                            setHistoryDateRange(
                                value
                            )
                        }
                    />
                </Col>
            </Row>

            <Row
                gutter={[10, 10]}
                justify="end"
                style={{
                    marginTop: 14
                }}
            >
                <Col
                    xs={24}
                    sm="auto"
                >
                    <Button
                        block={compact}
                        size="large"
                        onClick={
                            resetHistoryFilter
                        }
                    >
                        Reset
                    </Button>
                </Col>

                <Col
                    xs={24}
                    sm="auto"
                >
                    <Button
                        block={compact}
                        type="primary"
                        size="large"
                        icon={
                            <SearchOutlined />
                        }
                        loading={
                            historyLoading
                        }
                        onClick={
                            searchCustomHistory
                        }
                    >
                        Filter
                    </Button>
                </Col>
            </Row>

            <Divider />

            <Table
                rowKey="accurate_custom_history_id"
                loading={
                    historyLoading
                }
                columns={
                    historyColumns
                }
                dataSource={
                    historyData
                }
                scroll={{
                    x: 1450
                }}
                pagination={{
                    current:
                        historyPage,

                    pageSize:
                        historyPageSize,

                    total:
                        historyTotal,

                    showSizeChanger:
                        true,

                    pageSizeOptions: [
                        10,
                        20,
                        50,
                        100
                    ],

                    showTotal: (
                        total,
                        range
                    ) =>
                        `${range[0]}-${range[1]} dari ${total}`,

                    onChange: (
                        page,
                        pageSize
                    ) => {
                        setHistoryPage(
                            page
                        );

                        setHistoryPageSize(
                            pageSize
                        );

                        getCustomHistory({
                            page,
                            pageSize
                        });
                    }
                }}
            />
        </Card>
    );

    // =====================================================
    // RENDER
    // =====================================================

    return (
        <div
            style={{
                maxWidth: 1440,
                margin: '0 auto',
                padding:
                    compact
                        ? 12
                        : 24
            }}
        >
            <Helmet>
                <title>
                    Koreksi Accurate
                </title>
            </Helmet>

            <div
                style={{
                    marginBottom: 20
                }}
            >
                <Typography.Title
                    level={
                        compact
                            ? 4
                            : 3
                    }
                    style={{
                        marginBottom: 5
                    }}
                >
                    Koreksi Accurate
                </Typography.Title>

                <Typography.Text
                    type="secondary"
                >
                    Resend transaksi, retur manual
                    dan riwayat pengiriman custom
                    Accurate tanpa mengubah data POS.
                </Typography.Text>
            </div>

            {message && (
                <Alert
                    showIcon
                    closable
                    type={
                        message.type
                    }
                    message={
                        message.text
                    }
                    onClose={() =>
                        setMessage(null)
                    }
                    style={{
                        marginBottom: 16,
                        borderRadius: 12
                    }}
                />
            )}

            <Tabs
                activeKey={
                    activeTab
                }
                onChange={(key) => {
                    setActiveTab(key);

                    if (
                        key === 'history'
                    ) {
                        getCustomHistory({
                            page: 1
                        });
                    }
                }}
                items={[
                    {
                        key:
                            'transaction',

                        label: (
                            <Space>
                                <SyncOutlined />
                                Transaksi
                            </Space>
                        ),

                        children:
                            transactionContent
                    },

                    {
                        key:
                            'history',

                        label: (
                            <Space>
                                <HistoryOutlined />
                                Riwayat Custom
                            </Space>
                        ),

                        children:
                            historyContent
                    }
                ]}
            />

            {/* =================================================
                HISTORY DETAIL
            ================================================= */}

            <Modal
                title={
                    <Space>
                        <HistoryOutlined />
                        Detail History Custom Accurate
                    </Space>
                }
                open={
                    historyDetailOpen
                }
                width={
                    compact
                        ? '95%'
                        : 900
                }
                footer={[
                    <Button
                        key="close"
                        onClick={() =>
                            setHistoryDetailOpen(
                                false
                            )
                        }
                    >
                        Tutup
                    </Button>
                ]}
                onCancel={() =>
                    setHistoryDetailOpen(
                        false
                    )
                }
            >
                {historyDetail && (
                    <>
                        <Row
                            gutter={[12, 12]}
                        >
                            <Col
                                xs={24}
                                md={8}
                            >
                                <div
                                    style={
                                        infoBoxStyle
                                    }
                                >
                                    <Typography.Text
                                        type="secondary"
                                    >
                                        Tipe
                                    </Typography.Text>

                                    <br />

                                    {historyDetail.type ===
                                    'RETURN' ? (
                                        <Tag color="volcano">
                                            RETURN
                                        </Tag>
                                    ) : (
                                        <Tag color="blue">
                                            TRANSACTION
                                        </Tag>
                                    )}
                                </div>
                            </Col>

                            <Col
                                xs={24}
                                md={8}
                            >
                                <div
                                    style={
                                        infoBoxStyle
                                    }
                                >
                                    <Typography.Text
                                        type="secondary"
                                    >
                                        Status
                                    </Typography.Text>

                                    <br />

                                    {historyDetail.status ===
                                    'SUCCESS' ? (
                                        <Tag color="green">
                                            SUCCESS
                                        </Tag>
                                    ) : (
                                        <Tag color="red">
                                            FAILED
                                        </Tag>
                                    )}
                                </div>
                            </Col>

                            <Col
                                xs={24}
                                md={8}
                            >
                                <div
                                    style={
                                        infoBoxStyle
                                    }
                                >
                                    <Typography.Text
                                        type="secondary"
                                    >
                                        User
                                    </Typography.Text>

                                    <br />

                                    <Typography.Text
                                        strong
                                    >
                                        {historyDetail.username ||
                                            '-'}
                                    </Typography.Text>
                                </div>
                            </Col>
                        </Row>

                        <Divider />

                        <Row
                            gutter={[12, 12]}
                        >
                            <Col
                                xs={24}
                                md={12}
                            >
                                <Typography.Text
                                    type="secondary"
                                >
                                    Transaction Code
                                </Typography.Text>

                                <div
                                    style={{
                                        marginTop: 4
                                    }}
                                >
                                    <Typography.Text
                                        strong
                                        copyable
                                    >
                                        {
                                            historyDetail.transaction_code
                                        }
                                    </Typography.Text>
                                </div>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Typography.Text
                                    type="secondary"
                                >
                                    Document Number
                                </Typography.Text>

                                <div
                                    style={{
                                        marginTop: 4
                                    }}
                                >
                                    <Typography.Text
                                        strong
                                        copyable
                                    >
                                        {
                                            historyDetail.document_number
                                        }
                                    </Typography.Text>
                                </div>
                            </Col>
                        </Row>

                        <Divider />

                        <Row
                            gutter={[12, 12]}
                        >
                            <Col
                                xs={24}
                                md={8}
                            >
                                <Typography.Text
                                    type="secondary"
                                >
                                    Tanggal Accurate
                                </Typography.Text>

                                <br />

                                <Typography.Text
                                    strong
                                >
                                    {historyDetail.trans_date
                                        ? moment(
                                            historyDetail.trans_date
                                        ).format(
                                            'DD/MM/YYYY'
                                        )
                                        : '-'}
                                </Typography.Text>
                            </Col>

                            <Col
                                xs={24}
                                md={8}
                            >
                                <Typography.Text
                                    type="secondary"
                                >
                                    Total
                                </Typography.Text>

                                <br />

                                <Typography.Text
                                    strong
                                >
                                    {currencyFormat(
                                        Number(
                                            historyDetail.total_amount ||
                                            0
                                        )
                                    )}
                                </Typography.Text>
                            </Col>

                            <Col
                                xs={24}
                                md={8}
                            >
                                <Typography.Text
                                    type="secondary"
                                >
                                    Waktu
                                </Typography.Text>

                                <br />

                                <Typography.Text
                                    strong
                                >
                                    {historyDetail.created_at
                                        ? moment(
                                            historyDetail.created_at
                                        ).format(
                                            'DD/MM/YYYY HH:mm:ss'
                                        )
                                        : '-'}
                                </Typography.Text>
                            </Col>
                        </Row>

                        <Divider
                            orientation="left"
                        >
                            Request Payload
                        </Divider>

                        <pre
                            style={{
                                maxHeight: 320,
                                overflow: 'auto',
                                padding: 14,
                                borderRadius: 10,
                                background:
                                    '#f5f5f5',
                                fontSize: 12
                            }}
                        >
                            {JSON.stringify(
                                historyDetail.request_payload,
                                null,
                                2
                            )}
                        </pre>

                        <Divider
                            orientation="left"
                        >
                            Response Accurate
                        </Divider>

                        <pre
                            style={{
                                maxHeight: 320,
                                overflow: 'auto',
                                padding: 14,
                                borderRadius: 10,
                                background:
                                    '#f5f5f5',
                                fontSize: 12
                            }}
                        >
                            {JSON.stringify(
                                historyDetail.response_payload,
                                null,
                                2
                            )}
                        </pre>

                        {historyDetail.notes && (
                            <>
                                <Divider
                                    orientation="left"
                                >
                                    Notes
                                </Divider>

                                <Typography.Paragraph>
                                    {
                                        historyDetail.notes
                                    }
                                </Typography.Paragraph>
                            </>
                        )}
                    </>
                )}
            </Modal>
        </div>
    );
}

export default AccurateCustom;